import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiEdit3 } from 'react-icons/fi';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import { createOrganization, updateOrganization } from '../../utils/supabaseClient';

interface OrganizationSetupModalProps {
	onComplete: (organizationId: string) => void;
	organizationId: string
}

const OrganizationSetupModal: React.FC<OrganizationSetupModalProps> = ({ organizationId, onComplete }) => {
	const { t } = useTranslation();
	const [organizationName, setOrganizationName] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!organizationName.trim()) {
			setError(t('organizations.nameRequired'));
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const { data, error } = await updateOrganization(organizationId, { name: organizationName });

			if (error) throw error;
			if (!data) throw new Error('No data returned');

			onComplete(data.id);
		} catch (err) {
			console.error('Error creating organization:', err);
			setError(t('organizations.createError'));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
			<motion.div
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden"
			>
				<div className="p-6">
					<motion.div
						initial={{ y: -20 }}
						animate={{ y: 0 }}
						transition={{ delay: 0.1 }}
					>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
							{t('organizations.welcome')}
						</h2>
						<p className="text-gray-600 dark:text-gray-300 mb-6">
							{t('organizations.setupDescription')}
						</p>
					</motion.div>

					{error && (
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4"
						>
							{error}
						</motion.div>
					)}

					<form onSubmit={handleSubmit}>
						<motion.div
							initial={{ y: 20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{ delay: 0.2 }}
							className="mb-6"
						>
							<InputField
								label={t('organizations.name')}
								value={organizationName}
								onChange={(e) => setOrganizationName(e.target.value)}
								placeholder={t('organizations.namePlaceholder')}
								icon={<FiEdit3 />}
								required
								autoFocus
							/>
						</motion.div>

						<motion.div
							initial={{ y: 20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{ delay: 0.3 }}
						>
							<Button
								type="submit"
								fullWidth
								size="lg"
								isLoading={isSubmitting}
							>
								{t('organizations.createOrganization')}
							</Button>
						</motion.div>
					</form>
				</div>
			</motion.div>
		</div>
	);
};

export default OrganizationSetupModal;